import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
    UserIcon,
    ShoppingBagIcon,
    MapPinIcon,
    PhoneIcon,
    CalendarIcon,
    ChevronRightIcon,
    ArrowRightOnRectangleIcon,
    Cog6ToothIcon,
    CreditCardIcon,
    Bars3Icon,
    HomeIcon,
    TagIcon,
    Squares2X2Icon,
    ChevronUpIcon
} from '@heroicons/react/24/outline';
import { useAppContext } from '../context/AppContext';
import { API } from '../config';
import logo from '../images/FINAL-DESIGN-LOGO-01.png';

const ProfilePage = () => {
    const { user, logout } = useAppContext();
    const [orders, setOrders] = useState([]);
    const [activeTab, setActiveTab] = useState('orders'); // 'orders', 'settings'
    const [loading, setLoading] = useState(true);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [isProfileOpen, setIsProfileOpen] = useState(false);
    const [categories, setCategories] = useState([]);

    useEffect(() => {
        if (user) {
            fetchOrders();
        }
        fetchCategories();
    }, [user]);

    const fetchOrders = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API}/orders`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setOrders(response.data);
        } catch (error) {
            console.error('Failed to fetch orders:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await axios.get(`${API}/categories`);
            setCategories(response.data);
        } catch (error) {
            console.error('Failed to fetch categories:', error);
        }
    };

    if (!user) {
        return (
            <div className="min-h-screen bg-gray-50 pt-32 pb-12 flex items-center justify-center px-4">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white rounded-3xl shadow-sm p-12 text-center max-w-md w-full border border-gray-100"
                >
                    <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <UserIcon className="h-10 w-10 text-gray-400" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Please Login</h2>
                    <p className="text-gray-500 mb-8">Sign in to view your profile and order history.</p>
                    <Link to="/login" className="block w-full bg-gray-900 text-white px-6 py-3 rounded-xl hover:bg-black transition-all font-medium">
                        Sign In
                    </Link>
                    <p className="mt-4 text-sm text-gray-500">
                        Don't have an account? <Link to="/register" className="text-gray-900 font-medium hover:underline">Register</Link>
                    </p>
                </motion.div>
            </div>
        );
    }

    const tabs = [
        { id: 'orders', label: 'My Orders', icon: ShoppingBagIcon },
        { id: 'settings', label: 'Account Settings', icon: Cog6ToothIcon },
    ];

    return (
        <div className="flex min-h-screen bg-gray-50">
            {/* Mobile Sidebar Overlay */}
            {isSidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={() => setIsSidebarOpen(false)}
                />
            )}

            {/* Sidebar */}
            <aside className={`
                fixed lg:sticky top-0 left-0 z-50
                w-64 bg-[#111114] text-gray-400 flex flex-col
                h-screen
                transform transition-transform duration-300 ease-in-out
                ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
            `}>
                <div className="flex flex-col h-full">
                    <div className="p-6 flex-1 overflow-y-auto">
                        {/* Logo Area */}
                        <div className="flex items-center gap-3 mb-10 px-2">
                            <img src={logo} alt="Illustra" className="h-8 w-auto brightness-0 invert" />
                        </div>

                        <nav className="space-y-2">
                            <p className="px-4 text-xs font-semibold uppercase tracking-wider text-gray-500 mb-4">Menu</p>

                            <Link
                                to="/products"
                                className="w-full flex items-center px-4 py-3 rounded-xl transition-all duration-200 group hover:bg-[#1F1F23] hover:text-white"
                            >
                                <Squares2X2Icon className="h-5 w-5 mr-3" />
                                <span className="font-medium">All Products</span>
                            </Link>

                            <div className="pt-4">
                                <p className="px-4 text-xs font-semibold uppercase tracking-wider text-gray-500 mb-4">Categories</p>
                                {categories.map((category) => (
                                    <Link
                                        key={category.id}
                                        to={`/products?category_id=${category.id}`}
                                        className="w-full flex items-center px-4 py-3 rounded-xl transition-all duration-200 group hover:bg-[#1F1F23] hover:text-white"
                                    >
                                        <TagIcon className="h-5 w-5 mr-3" />
                                        <span className="font-medium">{category.name}</span>
                                    </Link>
                                ))}
                            </div>

                            <div className="pt-4 mt-4 border-t border-gray-800">
                                <p className="px-4 text-xs font-semibold uppercase tracking-wider text-gray-500 mb-4">Main Site</p>
                                <Link to="/" className="w-full flex items-center px-4 py-3 text-gray-400 hover:text-white hover:bg-[#1F1F23] rounded-xl transition-all">
                                    <HomeIcon className="h-5 w-5 mr-3" />
                                    <span className="font-medium">Home</span>
                                </Link>
                            </div>
                        </nav>
                    </div>

                    {/* User Profile Section at Bottom */}
                    {user && (
                        <div className="p-4 border-t border-gray-800 bg-[#0c0c0e]">
                            <div className="relative">
                                <button
                                    onClick={() => setIsProfileOpen(!isProfileOpen)}
                                    className="w-full flex items-center gap-3 p-2 rounded-xl hover:bg-[#1F1F23] transition-colors group"
                                >
                                    <div className="w-10 h-10 rounded-full bg-yellow-400 flex items-center justify-center text-yellow-900 font-bold text-sm shrink-0">
                                        {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                                    </div>
                                    <div className="flex-1 text-left overflow-hidden">
                                        <p className="text-sm font-medium text-white truncate">{user.name || 'User'}</p>
                                        <p className="text-xs text-gray-500 truncate">{user.email}</p>
                                    </div>
                                    <ChevronUpIcon className={`h-5 w-5 text-gray-500 transition-transform ${isProfileOpen ? 'rotate-180' : ''}`} />
                                </button>

                                {/* Dropdown Menu */}
                                {isProfileOpen && (
                                    <div className="absolute bottom-full left-0 w-full mb-2 bg-[#1F1F23] border border-gray-800 rounded-xl shadow-xl overflow-hidden p-1">
                                        <Link
                                            to="/profile"
                                            className="flex items-center gap-3 w-full px-4 py-3 text-sm text-gray-300 hover:bg-[#2a2a2f] hover:text-white rounded-lg transition-colors"
                                        >
                                            <UserIcon className="h-5 w-5" />
                                            Profile
                                        </Link>
                                        <button
                                            onClick={logout}
                                            className="flex items-center gap-3 w-full px-4 py-3 text-sm text-red-400 hover:bg-[#2a2a2f] hover:text-red-300 rounded-lg transition-colors"
                                        >
                                            <ArrowRightOnRectangleIcon className="h-5 w-5" />
                                            Sign Out
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </aside>

            {/* Main Content */}
            <main className="flex-1 min-w-0 overflow-x-hidden lg:ml-0">
                <header className="h-20 bg-white border-b border-gray-100 flex items-center justify-between px-4 lg:px-8 sticky top-0 z-30">
                    <div className="flex items-center gap-4">
                        <button onClick={() => setIsSidebarOpen(true)} className="lg:hidden p-2 hover:bg-gray-100 rounded-xl text-gray-600">
                            <Bars3Icon className="h-6 w-6" />
                        </button>
                        <div>
                            <h1 className="text-xl font-bold text-gray-900">My Profile</h1>
                            <p className="text-sm text-gray-500 hidden sm:block">Manage your account and orders</p>
                        </div>
                    </div>
                </header>

                <div className="p-4 lg:p-8">
                    <div className="max-w-6xl mx-auto">
                        {/* Tabs */}
                        <div className="flex gap-2 mb-6 border-b border-gray-200">
                            {tabs.map(tab => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`flex items-center gap-2 px-6 py-3 font-medium transition-all border-b-2 ${activeTab === tab.id
                                        ? 'border-gray-900 text-gray-900'
                                        : 'border-transparent text-gray-500 hover:text-gray-900'
                                        }`}
                                >
                                    <tab.icon className="h-5 w-5" />
                                    <span>{tab.label}</span>
                                </button>
                            ))}
                        </div>

                        {/* Content */}
                        <AnimatePresence mode="wait">
                            {activeTab === 'orders' && (
                                <motion.div
                                    key="orders"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -20 }}
                                    className="space-y-6"
                                >
                                    <div className="flex justify-between items-center mb-2">
                                        <h2 className="text-2xl font-bold text-gray-900">Order History</h2>
                                        <span className="text-sm text-gray-500">{orders.length} orders</span>
                                    </div>

                                    {loading ? (
                                        <div className="text-center py-12">
                                            <div className="animate-spin w-8 h-8 border-2 border-gray-900 border-t-transparent rounded-full mx-auto mb-4"></div>
                                            <p className="text-gray-500">Loading orders...</p>
                                        </div>
                                    ) : orders.length === 0 ? (
                                        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-12 text-center">
                                            <ShoppingBagIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                                            <h3 className="text-lg font-medium text-gray-900 mb-2">No orders yet</h3>
                                            <p className="text-gray-500 mb-6">Looks like you haven't made any purchases yet.</p>
                                            <Link
                                                to="/products"
                                                className="inline-flex items-center px-6 py-3 bg-gray-900 text-white rounded-xl hover:bg-black transition-all font-medium"
                                            >
                                                Start Shopping
                                            </Link>
                                        </div>
                                    ) : (
                                        <div className="space-y-4">
                                            {orders.map((order, index) => (
                                                <motion.div
                                                    key={order.id}
                                                    initial={{ opacity: 0, y: 20 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ delay: index * 0.05 }}
                                                    className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow"
                                                >
                                                    <div className="p-6">
                                                        <div className="flex flex-wrap justify-between items-start gap-4 mb-6">
                                                            <div>
                                                                <div className="flex items-center gap-3 mb-1">
                                                                    <h3 className="text-lg font-bold text-gray-900">Order #{order.id.slice(-8)}</h3>
                                                                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${order.status === 'completed' ? 'bg-green-100 text-green-800' :
                                                                        order.status === 'dispatched' ? 'bg-blue-100 text-blue-800' :
                                                                            'bg-yellow-100 text-yellow-800'
                                                                        }`}>
                                                                        {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                                                                    </span>
                                                                </div>
                                                                <p className="text-sm text-gray-500 flex items-center">
                                                                    <CalendarIcon className="h-4 w-4 mr-1.5" />
                                                                    {new Date(order.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
                                                                </p>
                                                            </div>
                                                            <div className="text-right">
                                                                <p className="text-2xl font-bold text-gray-900">₹{order.total_amount}</p>
                                                                <p className="text-sm text-gray-500">{order.items.length} items</p>
                                                            </div>
                                                        </div>

                                                        <div className="border-t border-gray-100 pt-6">
                                                            <div className="space-y-4">
                                                                {order.items.map((item, idx) => (
                                                                    <div key={idx} className="space-y-3">
                                                                        <div className="flex items-center gap-4">
                                                                            <div className="h-16 w-16 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0 border border-gray-100">
                                                                                {item.product_image || item.custom_image_url ? (
                                                                                    <img
                                                                                        src={item.product_image || item.custom_image_url}
                                                                                        alt={item.product_title || 'Product'}
                                                                                        className="h-full w-full object-cover"
                                                                                    />
                                                                                ) : (
                                                                                    <ShoppingBagIcon className="h-full w-full p-4 text-gray-300" />
                                                                                )}
                                                                            </div>
                                                                            <div className="flex-1">
                                                                                <h4 className="font-medium text-gray-900">{item.product_title || `Product ID: ${item.product_id}`}</h4>
                                                                                <p className="text-sm text-gray-500">Qty: {item.quantity} {item.size && `• Size: ${item.size}`}</p>
                                                                                {item.custom_designs && item.custom_designs.length > 0 && (
                                                                                    <p className="text-xs text-green-600 font-medium mt-1">
                                                                                        ✓ {item.custom_designs.length} page(s) customized
                                                                                    </p>
                                                                                )}
                                                                            </div>
                                                                            <p className="font-medium text-gray-900">₹{item.price}</p>
                                                                        </div>

                                                                        {/* Custom Designs Display */}
                                                                        {item.custom_designs && item.custom_designs.length > 0 && (
                                                                            <div className="ml-20 pl-4 border-l-2 border-gray-200">
                                                                                <p className="text-xs font-semibold text-gray-700 mb-2">Your Custom Design:</p>
                                                                                <div className="flex flex-wrap gap-2">
                                                                                    {item.custom_designs.map((design, dIdx) => (
                                                                                        <div key={dIdx} className="relative group">
                                                                                            <img
                                                                                                src={design.imageUrl}
                                                                                                alt={design.pageName}
                                                                                                className="h-24 w-auto rounded-lg border-2 border-gray-200 hover:border-gray-400 transition-colors shadow-sm"
                                                                                            />
                                                                                            <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-xs py-1 px-2 rounded-b-lg">
                                                                                                {design.pageName}
                                                                                            </div>
                                                                                        </div>
                                                                                    ))}
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </motion.div>
                                            ))}
                                        </div>
                                    )}
                                </motion.div>
                            )}

                            {activeTab === 'settings' && (
                                <motion.div
                                    key="settings"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -20 }}
                                    className="bg-white rounded-3xl shadow-sm border border-gray-100 p-8"
                                >
                                    <h2 className="text-2xl font-bold text-gray-900 mb-6">Account Settings</h2>
                                    <div className="space-y-8">
                                        <div className="grid md:grid-cols-2 gap-8">
                                            <div>
                                                <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Full Name</label>
                                                <div className="flex items-center px-4 py-3 bg-gray-50 rounded-xl border border-gray-200 text-gray-900">
                                                    <UserIcon className="h-5 w-5 text-gray-400 mr-3" />
                                                    {user.name}
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Email Address</label>
                                                <div className="flex items-center px-4 py-3 bg-gray-50 rounded-xl border border-gray-200 text-gray-900">
                                                    <span className="text-gray-400 mr-3">@</span>
                                                    {user.email}
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Phone Number</label>
                                                <div className="flex items-center px-4 py-3 bg-gray-50 rounded-xl border border-gray-200 text-gray-900">
                                                    <PhoneIcon className="h-5 w-5 text-gray-400 mr-3" />
                                                    {user.phone || 'Not provided'}
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Member Since</label>
                                                <div className="flex items-center px-4 py-3 bg-gray-50 rounded-xl border border-gray-200 text-gray-900">
                                                    <CalendarIcon className="h-5 w-5 text-gray-400 mr-3" />
                                                    {new Date(user.created_at).toLocaleDateString()}
                                                </div>
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Shipping Address</label>
                                            <div className="flex items-start px-4 py-3 bg-gray-50 rounded-xl border border-gray-200 text-gray-900">
                                                <MapPinIcon className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                                                {user.address || 'No address provided'}
                                            </div>
                                        </div>

                                        <div className="pt-6 border-t border-gray-100">
                                            <button className="bg-gray-900 text-white px-6 py-2.5 rounded-xl hover:bg-black transition-all font-medium text-sm shadow-sm">
                                                Edit Profile
                                            </button>
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default ProfilePage;
